import React, {useEffect, useMemo, useState} from "react";
import {Modal, ModalBody, ModalHeader} from "reactstrap";
import styled, {css} from "styled-components";
import {useCheatKey} from "../Utility/useCheatKey";
import {ItemIcon} from "../SharedComponents/ItemIcon";
import {TechnologyDefinition} from "../State/TechnologyData";
import {
    PuzzleDefinition,
    PuzzleState,
    STORAGE_PREFIX,
    buildPuzzleDefinition,
    getPaintUsed,
    hasPath,
    isEndpoint,
    keyFor,
    loadPuzzleState,
    serialize,
    setColorForKey,
} from "./wirePuzzleLogic";

const Board = styled.div<{ $size: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$size}, 32px);
  grid-template-rows: repeat(${props => props.$size}, 32px);
  gap: 4px;
  justify-content: center;
  padding: 8px;
  background: #f8fafc;
  border-radius: 12px;
`;

const Cell = styled.button<{ $type: string; $color?: string; $isActive: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  padding: 0;
  background: ${({$type, $color}) => {
    if ($type === "obstacle") return "#1e293b";
    if ($type === "endpoint") return $color ?? "#a21caf";
    if ($type === "wire" && $color) return $color;
    return "#e2e8f0";
  }};
  cursor: ${({$type}) => $type === "obstacle" ? "not-allowed" : "pointer"};
  opacity: ${({$isActive}) => $isActive ? 1 : 0.6};
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  ${({$isActive}) => $isActive && css`box-shadow: 0 0 0 2px rgba(14,165,233,0.45); transform: scale(1.02);`};
`;

const Controls = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Palette = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PaletteButton = styled.button<{ $active: boolean; $color: string }>`
  border-radius: 10px;
  border: 2px solid ${({$active, $color}) => $active ? $color : "#cbd5e1"};
  background: ${({$color}) => $color};
  color: #0f172a;
  padding: 6px 10px;
  cursor: pointer;
  font-weight: 700;
  box-shadow: ${({$active}) => $active ? "0 0 0 2px rgba(15,23,42,0.12)" : "none"};
`;

const Stat = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  background: #f8fafc;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
`;

const LegendDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({$color}) => $color};
  border: 1px solid rgba(15,23,42,0.25);
`;

type WirePuzzleModalProps = {
    technology: TechnologyDefinition;
    isOpen: boolean;
    onClose: () => void;
    onComplete: () => void;
};

export function WirePuzzleModal({technology, isOpen, onClose, onComplete}: WirePuzzleModalProps) {
    const cheatActive = useCheatKey("c");
    const [definition, setDefinition] = useState<PuzzleDefinition>(() => buildPuzzleDefinition(technology));
    const [state, setState] = useState<PuzzleState>(() => loadPuzzleState(definition, technology.id));

    useEffect(() => {
        const freshDefinition = buildPuzzleDefinition(technology);
        setDefinition(freshDefinition);
        setState(loadPuzzleState(freshDefinition, technology.id));
    }, [technology]);

    useEffect(() => {
        if (isOpen) serialize(definition, state, technology.id);
    }, [definition, state, technology.id, isOpen]);

    const activePair = useMemo(
        () => definition.pairs.find(pair => pair.id === state.activePair) ?? definition.pairs[0],
        [definition.pairs, state.activePair],
    );
    const paintUsed = getPaintUsed(state);
    const complete = useMemo(
        () => definition.pairs.every(pair => hasPath(definition, state.colors, pair)) && paintUsed <= definition.paintLimit,
        [definition, paintUsed, state.colors],
    );

    useEffect(() => {
        if (complete) {
            localStorage.removeItem(`${STORAGE_PREFIX}${technology.id}`);
            onComplete();
        }
    }, [complete, onComplete, technology.id]);

    const handleCellClick = (row: number, col: number) => {
        const pair = activePair;
        if (!pair) return;
        const key = keyFor({row, col});
        if (definition.obstacles.includes(key)) return;
        if (definition.pairs.some(other => isEndpoint(other, key) && other.id !== pair.id)) return;
        if (isEndpoint(pair, key)) return;
        if (state.colors[key] && state.colors[key] !== pair.color) return;
        setState(prev => setColorForKey(definition, prev, key, prev.colors[key] ? undefined : pair.color));
    };

    const undo = () => setState(prev => {
        const history = [...prev.history];
        const last = history.pop();
        if (!last) return prev;
        const colors = {...prev.colors};
        if (last.previous) colors[last.key] = last.previous; else delete colors[last.key];
        return {...prev, colors, history, future: [{key: last.key, previous: colors[last.key], next: last.next}, ...prev.future]};
    });

    const redo = () => setState(prev => {
        const next = prev.future[0];
        if (!next) return prev;
        const future = prev.future.slice(1);
        const colors = {...prev.colors};
        if (next.next) colors[next.key] = next.next; else delete colors[next.key];
        return {...prev, colors, history: [...prev.history, next], future};
    });

    const resetBoard = () => {
        const freshDefinition = buildPuzzleDefinition(technology);
        setDefinition(freshDefinition);
        setState({colors: {}, activePair: freshDefinition.pairs[0].id, history: [], future: []});
    };

    const autoComplete = () => {
        setState({
            colors: Object.fromEntries(definition.pairs.flatMap(pair => pair.basePath.map(coord => [keyFor(coord), pair.color]))),
            activePair: definition.pairs[0].id,
            history: [],
            future: [],
        });
    };

    useEffect(() => {
        if (cheatActive && isOpen) autoComplete();
    }, [cheatActive, isOpen]);

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg" centered>
            <ModalHeader toggle={onClose}>Connect the circuits to enable {technology.name}</ModalHeader>
            <ModalBody>
                <Controls>
                    <Stat><strong>Grid</strong> {definition.size} x {definition.size}</Stat>
                    <Stat><strong>Obstacles</strong> {definition.obstacles.length}</Stat>
                    <Stat><strong>Pairs</strong> {definition.pairs.length}</Stat>
                    <Stat><strong>Paint</strong> {paintUsed}/{definition.paintLimit}</Stat>
                    <div style={{display: "flex", gap: 8}}>
                        <button onClick={undo} disabled={!state.history.length}>Undo</button>
                        <button onClick={redo} disabled={!state.future.length}>Redo</button>
                        <button onClick={resetBoard}>New puzzle</button>
                        {cheatActive && <button onClick={autoComplete}>Cheat complete</button>}
                    </div>
                </Controls>
                <Palette>
                    {definition.pairs.map((pair, index) => (
                        <PaletteButton
                            key={pair.id}
                            $color={pair.color}
                            $active={pair.id === activePair?.id}
                            onClick={() => setState(prev => ({...prev, activePair: pair.id}))}
                        >
                            <LegendDot $color={pair.color}/> Pair {index + 1}
                        </PaletteButton>
                    ))}
                </Palette>
                <p style={{marginTop: 8}}>
                    Draw a single uninterrupted path for each color. Paths cannot cross and you cannot exceed the paint limit.
                    Progress autosaves, so you can close this modal and return later.
                </p>
                <Board $size={definition.size}>
                    {Array.from({length: definition.size}).map((_, row) =>
                        Array.from({length: definition.size}).map((_, col) => {
                            const key = `${row}-${col}`;
                            const obstacle = definition.obstacles.includes(key);
                            const endpointFor = definition.pairs.find(pair => isEndpoint(pair, key));
                            const colorHere = endpointFor ? endpointFor.color : state.colors[key];
                            const type = obstacle ? "obstacle" : endpointFor ? "endpoint" : colorHere ? "wire" : "empty";
                            return (
                                <Cell
                                    key={key}
                                    $type={type}
                                    $color={colorHere}
                                    $isActive={activePair ? (!endpointFor || endpointFor.id === activePair.id) : true}
                                    onClick={() => handleCellClick(row, col)}
                                />
                            );
                        }))
                    }
                </Board>
                {complete && (
                    <div style={{marginTop: 12, padding: 10, background: "#ecfdf3", borderRadius: 10, border: "1px solid #bbf7d0"}}>
                        <strong>Complete!</strong> Circuits linked. Research unlocks when you close this modal.
                    </div>
                )}
                <div style={{marginTop: 16, display: "flex", gap: 12, alignItems: "center"}}>
                    <ItemIcon itemId={technology.iconId} size={28}/>
                    <div>
                        <div>{technology.description}</div>
                        {technology.reward && <small>Reward: {technology.reward}</small>}
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
}
